import { Button } from 'designSystem/components/Button'
import {
  KNOWLEDGE_DOC_SIDEBAR_TOGGLE,
  useGeckoAnalytics,
} from 'features/analytics'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { ArrowLeft, ArrowRight, FileText, PanelLeft, Star } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { DocumentHeaderTitle } from './DocumentHeaderTitle'
import { DownloadDocumentButton } from './DownloadDocumentButton'
import { PageSelector } from './PageSelector'
import { PrintDocumentButton } from './PrintDocumentButton'
import { SearchResultsNavigator } from './SearchResultsNavigator'
import { SettingsMenu } from './SettingsMenu'

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  gap: 0.75rem;
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const TrailingButtonsContainer = styled(ButtonsContainer)`
  margin-left: auto;
`

const BackFwdContainer = styled(ButtonsContainer)`
  gap: 1px;
`

const PageSelectorContainer = styled.div`
  margin-right: 1rem;
`

const FavoriteStarButton = styled(Button)<{
  $isFavorited: boolean
}>`
  color: ${(props) =>
    props.$isFavorited
      ? props.theme.forecast.defaultText
      : props.theme.forecast.secondaryText};

  &:hover {
    color: ${(props) => props.theme.forecast.defaultText};
  }
`

interface KnowledgeDocumentHeaderBarProps {
  isThumbnailSidebarOpen: boolean
  onToggleThumbnailSidebar: () => void
  isMultiPage: boolean
}

export function KnowledgeDocumentHeaderBar({
  isThumbnailSidebarOpen,
  onToggleThumbnailSidebar,
  isMultiPage,
}: KnowledgeDocumentHeaderBarProps) {
  const { trackEvent } = useGeckoAnalytics()
  const {
    currentDocumentId,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    activeTabId,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    isOnLinkedDocuments,
    currentLinkedDocumentsData,
    activeHistoryItem,
  } = useKnowledgeDocumentUXContext()

  // Present only after a doc-reference (OPC / drawing-number) jump: an
  // explicit way back to the exact prior state, on top of the generic back.
  const referenceOrigin =
    activeHistoryItem?.type === 'document'
      ? activeHistoryItem.referenceOrigin
      : undefined

  const { systemDocumentEntries } = useKnowledgeDocumentDataContext()
  const { isFavorited, toggleFavorite } = useSearchActions()

  const currentDocTableRow = useMemo(() => {
    const entry = systemDocumentEntries.find(
      (documentEntry) => documentEntry.entry.id === currentDocumentId
    )
    if (!entry) return null
    return transformSystemDocumentToTableRow(entry)
  }, [currentDocumentId, systemDocumentEntries])

  const isCurrentFavorited = currentDocumentId
    ? isFavorited(currentDocumentId)
    : false

  const handleToggleFavorite = useCallback(() => {
    if (currentDocTableRow) {
      toggleFavorite(currentDocTableRow)
    }
  }, [currentDocTableRow, toggleFavorite])

  function handleSidebarToggle() {
    trackEvent(KNOWLEDGE_DOC_SIDEBAR_TOGGLE.title, {
      action: isSidebarCollapsed ? 'expand' : 'collapse',
    })
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }

  return (
    <>
      <StyledHeader>
        <BackFwdContainer>
          <Button
            variant='inputRaised'
            size='icon-lg'
            title='Back'
            disabled={!canGoBack}
            onClick={() => goBack(activeTabId)}
            style={{ borderRadius: '0.25rem 0 0 0.25rem' }}>
            <ArrowLeft size={18} />
          </Button>
          <Button
            variant='inputRaised'
            size='icon-lg'
            title='Forward'
            disabled={!canGoForward}
            onClick={() => goForward(activeTabId)}
            style={{ borderRadius: '0 0.25rem 0.25rem 0' }}>
            <ArrowRight size={18} />
          </Button>
        </BackFwdContainer>

        {referenceOrigin && (
          <Button
            variant='inputRaised'
            size='default'
            title={`Back to ${referenceOrigin.label}`}
            onClick={() => goBack(activeTabId)}>
            <ArrowLeft size={14} />
            Back to {referenceOrigin.label}
          </Button>
        )}

        {!isOnLinkedDocuments && isMultiPage && (
          <Button
            variant='inputRaised'
            size='icon-lg'
            onClick={onToggleThumbnailSidebar}
            title={
              isThumbnailSidebarOpen
                ? 'Hide Page Thumbnails'
                : 'Show Page Thumbnails'
            }>
            <PanelLeft size={18} strokeWidth={1.5} />
          </Button>
        )}

        <DocumentHeaderTitle
          documentId={currentDocumentId}
          customTitle={
            currentLinkedDocumentsData
              ? `References to ${currentLinkedDocumentsData.referenceText}`
              : undefined
          }
        />

        <TrailingButtonsContainer>
          {!isOnLinkedDocuments && (
            <>
              <PageSelectorContainer>
                <PageSelector />
              </PageSelectorContainer>
              <SearchResultsNavigator />
              {currentDocumentId && currentDocTableRow && (
                <FavoriteStarButton
                  variant='inputRaised'
                  size='icon-lg'
                  $isFavorited={isCurrentFavorited}
                  onClick={handleToggleFavorite}
                  title={
                    isCurrentFavorited
                      ? 'Remove from favorites'
                      : 'Add to favorites'
                  }>
                  <Star
                    size={18}
                    strokeWidth={1.5}
                    fill={isCurrentFavorited ? 'currentColor' : 'none'}
                  />
                </FavoriteStarButton>
              )}
              <PrintDocumentButton />
              <DownloadDocumentButton />

              <SettingsMenu />

              <Button
                variant='inputRaised'
                size='icon-lg'
                onClick={handleSidebarToggle}
                title={isSidebarCollapsed ? 'Show Metadata' : 'Hide Metadata'}>
                <FileText size={18} strokeWidth={1.5} />
              </Button>
            </>
          )}
        </TrailingButtonsContainer>
      </StyledHeader>
    </>
  )
}

export default KnowledgeDocumentHeaderBar
